/** Role helpers for admin UI. Hardcoded bypass: admin + super_admin only. Everyone else: Roles matrix. */

/** Latest /auth/me user — kept in sync by AppLayout so matrix changes apply without full reload. */
let liveAuthUser = null;

export const PERMISSIONS_CHANGED_EVENT = 'sft:permissions-changed';

export function setLiveAuthUser(user) {
  liveAuthUser = user || null;
}

export function getLiveAuthUser() {
  return liveAuthUser;
}

/** Notify app shells to re-fetch /auth/me (call after Roles save). */
export function notifyPermissionsChanged() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PERMISSIONS_CHANGED_EVENT));
    }
  } catch (_) { /* ignore */ }
}

export function primaryRole(user) {
  return String((liveAuthUser || user)?.role || user?.role || '').trim().toLowerCase();
}

/** Hardcoded full access — only admin and super_admin. */
export function isHardAdmin(user) {
  const r = primaryRole(user);
  return r === 'admin' || r === 'super_admin';
}

export function userHasRole(user, roleName) {
  const u = liveAuthUser || user;
  if (!u || !roleName) return false;
  const want = String(roleName).toLowerCase();
  if (String(u.role || '').trim().toLowerCase() === want) return true;
  const extras = Array.isArray(u.roles) ? u.roles.map((r) => String(r).toLowerCase()) : [];
  return extras.includes(want);
}

/**
 * Permission matrix helper: view | edit on a resource id.
 * Prefers live /auth/me permissions so Roles admin changes apply immediately.
 * admin / super_admin always allowed.
 */
export function canAccess(user, resourceId, level = 'view') {
  const u = liveAuthUser || user;
  if (!u || !resourceId) return false;
  if (isHardAdmin(u)) return true;
  const perms = (liveAuthUser && liveAuthUser.permissions) || u.permissions;
  const have = perms?.[resourceId];
  if (level === 'edit') return have === 'edit';
  return have === 'view' || have === 'edit';
}

/** True if user can access any of the given resource ids. */
export function canAccessAny(user, resourceIds, level = 'view') {
  return (resourceIds || []).some((id) => canAccess(user, id, level));
}

/** @deprecated Prefer canAccess — kept for call sites; maps to sales permissions. */
export function isSalesStaff(user) {
  if (isHardAdmin(user)) return true;
  return canAccessAny(user, [
    'sales-leads', 'sales-dashboard', 'sales-kpi', 'sales-reminders',
    'sales-developers', 'sales-lead-sources', 'sales-webshop', 'email-campaigns',
    'marketing-presentations'
  ], 'view');
}

/** Analytics → Proposals (margin report). */
export function canViewProposalAnalytics(user) {
  return canAccess(user, 'analytics-proposals', 'view');
}
